// The app's door to the analytics-and-ads consent cookie.
//
// The format, the parsing and the browser API are written once, in
// marketing/consent.js, because the marketing site reads the same cookie and
// its bundle may not import anything from src/ (that file's header says why it
// lives there). This module adds the two things only the app knows: the
// brand's domain, so the cookie is shared with the marketing site, and a way
// for the settings entry to reopen the banner.
//
// Anything that loads analytics or an ad tag imports `getConsent` /
// `onConsentChange` from here and follows docs/operations.md §5a:
// src/lib/analytics.js (#458) and src/lib/adConversions.js (#754), which both
// tell Google the same thing about the answer (`CONSENT_MODE`), and
// src/lib/metaPixel.js (#780), which tells Meta. `getConsent()` is what is in
// force: a grant unless the visitor declined or the browser sends Global
// Privacy Control (#755). `getConsentAnswer()` and `needsConsentNotice()` are
// for the banner. marketing/consent.js says why.

import { BRAND } from '../domain/brand.js';
import {
  CONSENT_MODE, configureConsent, getConsent, getConsentAnswer, needsConsentNotice, onConsentChange,
  privacySignalOn, renewConsent, setConsent,
} from '../../marketing/consent.js';

configureConsent({ domain: BRAND.domain });
renewConsent();

export {
  CONSENT_MODE, getConsent, getConsentAnswer, needsConsentNotice, onConsentChange, privacySignalOn, setConsent,
};
// The banner's status lines and button label, the marketing site's words too.
export { CONSENT_STATUS, allowLabel, consentStatus } from '../../marketing/consentBanner.js';

// The privacy policy and the terms are the marketing site's pages. Both keep
// their `.html`: hosting serves the site with `cleanUrls` off, so the bare path
// is a 404. The links that open them are src/components/LegalLinks.jsx.
export const PRIVACY_POLICY_URL = `https://${BRAND.domain}/privacy.html`;
export const TERMS_URL = `https://${BRAND.domain}/terms.html`;

// The FAQ (#576) is the marketing site's page too.
export const FAQ_URL = `https://${BRAND.domain}/faq.html`;

const openers = new Set();

/** Ask the banner to show itself, whatever the current answer. */
export function openConsentSettings() {
  for (const fn of openers) fn();
}

/** The banner's half of `openConsentSettings`. Returns the unsubscribe. */
export function onConsentSettingsOpen(fn) {
  openers.add(fn);
  return () => openers.delete(fn);
}
